import { usePaymentTerm } from "@faire/deprecated-retailer-visitor-shared/hooks/retailer/usePaymentTerm";
import { isLoggedInRetailer } from "@faire/deprecated-retailer-visitor-shared/lib/isLoggedInRetailer";
import {
  getSettingSpecialHolidayNet90TimeRange,
  getSettingSpecialHolidayNet90Timezone,
  specialHolidayNet90Active,
} from "@faire/deprecated-retailer-visitor-shared/settings/getSettingSpecialHolidayNet90";
import { exhaustiveSwitchWithDefault } from "@faire/web/common/exhaustiveSwitchWithDefault";
import { useStrictLocalization } from "@faire/web/common/localization";
import { intlDateWithTimezone } from "@faire/web/common/localization/date-fns/intlDate";
import { intlDate } from "@faire/web/common/localization/date-fns/intlDate";
import { DEPRECATED_backwardsCompatibleValues } from "@faire/web/common/localization/localize";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { ResponsiveContentV2 } from "@faire/web/ui/ResponsiveContentV2";
import { Medium } from "@faire/web/ui/Typography/Span";
import { addDays } from "date-fns/addDays";
import { differenceInHours } from "date-fns/differenceInHours";
import { differenceInMinutes } from "date-fns/differenceInMinutes";
import React, { type JSX } from "react";

enum PromoTextScenario {
  NOT_APPLICABLE,
  ACTIVE_TERMS_ELIGIBLE_LONG,
  ACTIVE_TERMS_ELIGIBLE_SHORT,
  ACTIVE_NO_TERMS_LONG,
  ACTIVE_NO_TERMS_SHORT,
  EXPIRING_TERMS_ELIGIBLE_LONG,
  EXPIRING_TERMS_ELIGIBLE_SHORT,
  EXPIRING_NO_TERMS_LONG,
  EXPIRING_NO_TERMS_SHORT,
}

export const SpecialHolidayNet90PromoBanner: React.FC = () => {
  const { strictLocalize } = useStrictLocalization();
  const isExtensionLaunched = !!getSettingSpecialHolidayNet90Timezone();
  const { isRetailerOnNetTerms } = usePaymentTerm();

  const endTime =
    (getSettingSpecialHolidayNet90TimeRange().end_at?.seconds ?? 0) * 1000;

  const endDateText = !getSettingSpecialHolidayNet90Timezone()
    ? strictLocalize({
        id: "SPmUvI",
        defaultMessage: "Oct 10",
        description: { text: "The ending date of the Net 90 promotion" },
      })
    : intlDateWithTimezone(
        endTime,
        "MMM d",
        getSettingSpecialHolidayNet90Timezone()
      );

  const hoursLeft = differenceInHours(endTime, new Date());

  const minutesLeft = Math.max(differenceInMinutes(endTime, new Date()), 1);

  const textPrefix = (): React.ReactNode => {
    if (!specialHolidayNet90Active()) {
      return null;
    }

    if (hoursLeft < 1) {
      return (
        <ResponsiveContentV2
          mobile={
            <StrictLocalMsg
              id="1veLlO"
              defaultMessage="{minutes, plural, one{{minutes} minute left!} other{{minutes} minutes left!}}"
              description={{
                text: "Promotion is ending soon and we are nudging retailer to participate",
              }}
              values={DEPRECATED_backwardsCompatibleValues({
                minutes: minutesLeft,
              })}
            />
          }
          tabletAndAbove={
            <StrictLocalMsg
              id="Uo6qCU"
              defaultMessage="{minutes, plural, one{Only {minutes} minute left!} other{Only {minutes} minutes left!}}"
              description={{
                text: "Promotion is ending soon and we are nudging retailer to participate",
              }}
              values={DEPRECATED_backwardsCompatibleValues({
                minutes: minutesLeft,
              })}
            />
          }
        />
      );
    }

    if (hoursLeft < 24) {
      return (
        <ResponsiveContentV2
          mobile={
            <StrictLocalMsg
              id="VSSdsJ"
              defaultMessage="{hoursLeft, plural, one{{hoursLeft} hour left!} other{{hoursLeft} hours left!}}"
              description={{
                text: "Promotion is ending soon and we are nudging retailer to participate",
              }}
              values={DEPRECATED_backwardsCompatibleValues({
                hoursLeft: hoursLeft,
              })}
            />
          }
          tabletAndAbove={
            <StrictLocalMsg
              id="4VqUkl"
              defaultMessage="{hoursLeft, plural, one{Only {hoursLeft} hour left!} other{Only {hoursLeft} hours left!}}"
              description={{
                text: "Promotion is ending soon and we are nudging retailer to participate",
              }}
              values={DEPRECATED_backwardsCompatibleValues({
                hoursLeft: hoursLeft,
              })}
            />
          }
        />
      );
    }

    if (isExtensionLaunched) {
      return (
        <ResponsiveContentV2
          tabletAndAbove={
            <StrictLocalMsg
              id="6XH9W/"
              defaultMessage="Limited time only!"
              description={{
                text: "Callout that this promotion does not go forever",
              }}
            />
          }
        />
      );
    }

    return (
      <ResponsiveContentV2
        mobile={
          <StrictLocalMsg
            id="BBAqNc"
            defaultMessage="Holiday buying is here!"
            description={{ text: "Introduce our new promotion" }}
          />
        }
        tabletAndAbove={
          <StrictLocalMsg
            id="fMMSoC"
            defaultMessage="Holiday special!"
            description={{
              text: "Introduce our new promotion",
            }}
            values={DEPRECATED_backwardsCompatibleValues({
              hoursLeft: hoursLeft,
            })}
          />
        }
      />
    );
  };

  const scenario = (isTabletAndAbove: boolean): PromoTextScenario => {
    if (!specialHolidayNet90Active()) {
      return PromoTextScenario.NOT_APPLICABLE;
    }

    const prefix = hoursLeft < 24 ? "EXPIRING_" : "ACTIVE_";
    const termsEligibility =
      !isLoggedInRetailer() || isRetailerOnNetTerms
        ? "TERMS_ELIGIBLE"
        : "NO_TERMS";
    const postfix = isTabletAndAbove ? "_LONG" : "_SHORT";

    return (
      PromoTextScenario[`${prefix}${termsEligibility}${postfix}`] ??
      PromoTextScenario.NOT_APPLICABLE
    );
  };

  const textBody = (isTabletAndAbove: boolean): JSX.Element | undefined => {
    if (isExtensionLaunched) {
      switch (scenario(isTabletAndAbove)) {
        case PromoTextScenario.ACTIVE_TERMS_ELIGIBLE_LONG:
        case PromoTextScenario.EXPIRING_TERMS_ELIGIBLE_LONG:
        case PromoTextScenario.ACTIVE_TERMS_ELIGIBLE_SHORT:
          return (
            <StrictLocalMsg
              id="v4mr8z"
              defaultMessage="Buy now, pay 90 days later—now through {endDateText}."
              description={{
                text: "Introducing holiday promotion and nudge user to place order",
              }}
              values={DEPRECATED_backwardsCompatibleValues({ endDateText })}
            />
          );
        case PromoTextScenario.EXPIRING_TERMS_ELIGIBLE_SHORT:
          return (
            <StrictLocalMsg
              id="qCT1ek"
              defaultMessage="Buy now, pay 90 days later."
              description={{
                text: "Introducing holiday promotion and nudge user to place order",
              }}
            />
          );
        case PromoTextScenario.ACTIVE_NO_TERMS_LONG:
        case PromoTextScenario.EXPIRING_NO_TERMS_LONG:
          return (
            <StrictLocalMsg
              id="m2Sxoy"
              defaultMessage="Enjoy extended 90-day returns on all opening orders—now through {endDateText}."
              description={{
                text: "Introducing holiday promotion and nudge user to place order",
              }}
              values={DEPRECATED_backwardsCompatibleValues({ endDateText })}
            />
          );
        case PromoTextScenario.ACTIVE_NO_TERMS_SHORT:
          return (
            <StrictLocalMsg
              id="MURMUH"
              defaultMessage="90-day returns on opening orders. Offer ends soon!"
              description={{
                text: "Introducing holiday promotion and nudge user to place order",
              }}
            />
          );
        case PromoTextScenario.EXPIRING_NO_TERMS_SHORT:
          return (
            <StrictLocalMsg
              id="BUIR/c"
              defaultMessage="90-day returns on opening orders."
              description={{
                text: "Introducing holiday promotion and nudge user to place order",
              }}
            />
          );
        default:
          exhaustiveSwitchWithDefault();
      }
      return undefined;
    }

    switch (scenario(isTabletAndAbove)) {
      case PromoTextScenario.ACTIVE_TERMS_ELIGIBLE_LONG:
        return (
          <StrictLocalMsg
            id="t8KSFP"
            defaultMessage="Now through {endDateText}, buy now & don't pay until {dueDate}—just in time for the holiday rush."
            description={{
              text: "Introducing holiday promotion and nudge user to place order",
            }}
            values={DEPRECATED_backwardsCompatibleValues({
              endDateText,
              dueDate: intlDate(addDays(new Date(), 90), "MMM d"),
            })}
          />
        );
      case PromoTextScenario.ACTIVE_TERMS_ELIGIBLE_SHORT:
      case PromoTextScenario.EXPIRING_TERMS_ELIGIBLE_SHORT:
        return (
          <StrictLocalMsg
            id="qCT1ek"
            defaultMessage="Buy now, pay 90 days later."
            description={{
              text: "Introducing holiday promotion and nudge user to place order",
            }}
          />
        );
      case PromoTextScenario.ACTIVE_NO_TERMS_LONG:
        return (
          <StrictLocalMsg
            id="sHXFla"
            defaultMessage="Now through {endDateText}, enjoy extended 90-day returns on opening orders."
            description={{
              text: "Introducing holiday promotion and nudge user to place order",
            }}
            values={DEPRECATED_backwardsCompatibleValues({ endDateText })}
          />
        );
      case PromoTextScenario.ACTIVE_NO_TERMS_SHORT:
      case PromoTextScenario.EXPIRING_NO_TERMS_SHORT:
        return (
          <StrictLocalMsg
            id="BUIR/c"
            defaultMessage="90-day returns on opening orders."
            description={{
              text: "Introducing holiday promotion and nudge user to place order",
            }}
          />
        );
      case PromoTextScenario.EXPIRING_TERMS_ELIGIBLE_LONG:
        return (
          <StrictLocalMsg
            id="dV9/gy"
            defaultMessage="Buy now, pay 90 days later—just in time for the holiday rush."
            description={{
              text: "Calling out that promotion is ending soon",
            }}
            values={DEPRECATED_backwardsCompatibleValues({ hoursLeft })}
          />
        );
      case PromoTextScenario.EXPIRING_NO_TERMS_LONG:
        return (
          <StrictLocalMsg
            id="16XzMy"
            defaultMessage="Enjoy extended 90-day returns on opening orders this holiday season."
            description={{
              text: "Calling out that promotion is ending soon",
            }}
            values={DEPRECATED_backwardsCompatibleValues({ hoursLeft })}
          />
        );
      default:
        return undefined;
    }
  };

  // if both mobile and desktop scenarios are false
  if (
    scenario(true) === PromoTextScenario.NOT_APPLICABLE &&
    scenario(false) === PromoTextScenario.NOT_APPLICABLE
  ) {
    return null;
  }

  return (
    <Typography align="center">
      <Medium>{textPrefix()}</Medium>
      <ResponsiveContentV2
        mobile={textBody(false)}
        tabletAndAbove={textBody(true)}
      />
    </Typography>
  );
};
